import React, { useState } from 'react';
import { CalcCard, CalcField, ResultStat, GrowthChart, CalcFooter, formatINR, sipFutureValue, SERIES_INVESTED, SERIES_VALUE } from './shared';

const corpusNeeded = (annualExpense, postRate, inflation, years) => {
  const real = (1 + postRate / 100) / (1 + inflation / 100) - 1;
  if (Math.abs(real) < 1e-9) return annualExpense * years;
  return annualExpense * ((1 - Math.pow(1 + real, -years)) / real) * (1 + real);
};

export const RetirementCalculator = () => {
  const [age, setAge] = useState(30);
  const [retireAge, setRetireAge] = useState(60);
  const [lifeAge, setLifeAge] = useState(85);
  const [expense, setExpense] = useState(40000);
  const [inflation, setInflation] = useState(6);
  const [preRate, setPreRate] = useState(12);
  const [postRate, setPostRate] = useState(7);

  const yearsToRetire = Math.max(1, Math.round(retireAge - age));
  const yearsInRetirement = Math.max(1, Math.round(lifeAge - Math.max(retireAge, age + 1)));
  const futureMonthlyExpense = expense * Math.pow(1 + inflation / 100, yearsToRetire);
  const corpus = corpusNeeded(futureMonthlyExpense * 12, postRate, inflation, yearsInRetirement);
  const monthlySip = corpus / sipFutureValue(1, preRate, yearsToRetire * 12);

  const data = Array.from({ length: yearsToRetire }, (_, i) => ({
    label: `Age ${Math.round(age) + i + 1}`,
    invested: monthlySip * 12 * (i + 1),
    value: sipFutureValue(monthlySip, preRate, 12 * (i + 1)),
  }));

  return (
    <CalcCard title="Retirement Calculator" subtitle="Estimate the corpus you may need and the monthly SIP to work towards it." testId="retirement-calculator">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-8">
        <CalcField label="Current Age" value={age} onChange={setAge} min={18} max={65} testId="retirement-age" />
        <CalcField label="Retirement Age" value={retireAge} onChange={setRetireAge} min={40} max={75} testId="retirement-retirement-age" />
        <CalcField label="Life Expectancy (Age)" value={lifeAge} onChange={setLifeAge} min={60} max={100} testId="retirement-life" />
        <CalcField label="Current Monthly Expenses (₹)" value={expense} onChange={setExpense} min={5000} max={500000} step={1000} testId="retirement-expense" />
        <CalcField label="Assumed Inflation (%)" value={inflation} onChange={setInflation} min={0} max={12} step={0.5} testId="retirement-inflation" />
        <CalcField label="Assumed Return Before Retirement (%)" value={preRate} onChange={setPreRate} min={1} max={18} step={0.5} testId="retirement-returns" />
        <CalcField label="Assumed Return After Retirement (%)" value={postRate} onChange={setPostRate} min={1} max={12} step={0.5} testId="retirement-post-returns" />
      </div>
      {retireAge <= age && (
        <p className="text-sm font-body text-[#A0451E] bg-white border border-[#D97706]/40 rounded-lg px-4 py-3 mb-4" data-testid="retirement-age-warning">
          Retirement age should be higher than your current age. Results below assume 1 year to retirement.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <ResultStat label="Monthly Expenses at Retirement" value={formatINR(futureMonthlyExpense)} testId="retirement-future-expense-result" />
        <ResultStat label="Illustrative Corpus Required" value={formatINR(corpus)} testId="retirement-corpus-result" accent />
        <ResultStat label="Monthly SIP Needed" value={formatINR(monthlySip)} testId="retirement-sip-result" />
      </div>
      <GrowthChart data={data} series={[SERIES_INVESTED, SERIES_VALUE]} testId="retirement-chart" />
      <CalcFooter testId="retirement" />
    </CalcCard>
  );
};
